import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Box, 
  Menu, 
  MenuItem, 
  Avatar, 
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Menu as MenuIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import PerfilDrawer from './PerfilDrawer';

const MainAppBar = ({ open, handleDrawerOpen, drawerWidth }) => {
  const navigate = useNavigate();
  const { user, logout, userRoles } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [perfilDrawerOpen, setPerfilDrawerOpen] = useState(false);
  const { isDark, toggleTheme } = useThemeContext();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const handlePerfilClick = () => {
    handleClose();
    setPerfilDrawerOpen(true);
  };

  const handleFotoAtualizada = (novaFoto) => {
    if (user) {
      const updatedUser = { ...user, foto_perfil: novaFoto };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const getUserInitials = () => {
    if (user?.nome) {
      const names = user.nome.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return user.nome.substring(0, 2).toUpperCase();
    }
    return 'OP';
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backgroundColor: 'background.paper',
        color: 'text.primary',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        transition: (theme) => theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        ...(open && {
          marginLeft: drawerWidth,
          width: `calc(100% - ${drawerWidth}px)`,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }),
        '& svg circle[fill*="url(#a-cl-1)"]': {
          display: 'none',
        },
        '& circle[cx="316.5"][cy="316.5"][r="316.5"]': {
          display: 'none',
        },
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{
            marginRight: isMobile ? 2 : 5,
            ...(open && { display: 'none' }),
          }}
        >
          <MenuIcon />
        </IconButton>
        <Typography 
          variant={isMobile ? 'body1' : 'h6'} 
          noWrap 
          component="div" 
          sx={{ 
            flexGrow: 1,
            fontSize: isMobile ? '0.875rem' : undefined,
            fontWeight: isMobile ? 500 : undefined,
          }}
        >
          {isMobile ? 'NEO' : 'Núcleo de Eficiência Operacional'}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={isDark ? 'Modo Claro' : 'Modo Escuro'}>
            <IconButton
              onClick={toggleTheme}
              color="inherit"
              sx={{ mr: 1 }}
            >
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar 
              src={user?.foto_perfil || ''}
              sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText' }}
            >
              {!user?.foto_perfil && getUserInitials()}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {user && (
              <MenuItem disabled>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user.nome}
                </Typography>
              </MenuItem>
            )}
            <MenuItem onClick={handlePerfilClick}>Perfil</MenuItem>
            <MenuItem onClick={handleClose}>Minha Conta</MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              Sair
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <PerfilDrawer
        open={perfilDrawerOpen}
        onClose={() => setPerfilDrawerOpen(false)}
        user={user}
        userRoles={userRoles}
        onFotoAtualizada={handleFotoAtualizada}
      />
    </AppBar>
  );
};

export default MainAppBar;
